import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PasswordResetProps {
  onSubmit: (passwords: { newPassword: string; confirmPassword: string }) => void;
  isSubmitting: boolean;
}

export const PasswordReset: React.FC<PasswordResetProps> = ({ onSubmit, isSubmitting }) => {
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<{
    newPassword?: string;
    confirmPassword?: string;
  }>({});

  const validate = (): boolean => {
    const newErrors: typeof errors = {};

    if (!newPassword) {
      newErrors.newPassword = t('settings.password.required');
    } else if (newPassword.length < 8) {
      newErrors.newPassword = t('settings.password.minLength');
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = t('settings.password.confirmRequired');
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = t('settings.password.mismatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({ newPassword, confirmPassword });
    }
  };

  return (
    <div className="flex-1 overflow-y-auto bg-black">
      <div className="flex flex-col lg:flex-row min-h-[486px] border-b border-greyP5 p-4 md:p-8">
        <div className="w-full lg:w-[30%] lg:pr-8 mb-6 lg:mb-0">
          <div className="flex flex-col justify-center">
            <div className="p-0 lg:p-5 text-white text-sm md:text-md w-full">
              <p>{t('settings.password.hint')}</p>
            </div>
          </div>
        </div>

        <div className="w-full lg:w-[70%] lg:pl-8">
          <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
            <div className="flex flex-col pt-0 md:pt-5">
              <label className="text-white text-sm md:text-md font-medium mb-2">
                {t('settings.password.newPassword')}
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setErrors((prev) => ({ ...prev, newPassword: undefined }));
                }}
                disabled={isSubmitting}
                className="w-full md:w-2/3 px-4 py-2 bg-greyP4 border border-gray-600 rounded text-white text-sm
                       focus:outline-none focus:border-green01 transition-colors duration-200
                       disabled:opacity-50"
                placeholder="••••••••"
              />
              {errors.newPassword && <p className="mt-1 text-red-500 text-xs">{errors.newPassword}</p>}
            </div>

            <div className="flex flex-col">
              <label className="text-white text-sm md:text-md font-medium mb-2">
                {t('settings.password.confirmPassword')}
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
                }}
                disabled={isSubmitting}
                className="w-full md:w-2/3 px-4 py-2 bg-greyP4 border border-gray-600 rounded text-white text-sm
                       focus:outline-none focus:border-green01 transition-colors duration-200
                       disabled:opacity-50"
                placeholder="••••••••"
              />
              {errors.confirmPassword && <p className="mt-1 text-red-500 text-xs">{errors.confirmPassword}</p>}
            </div>
          </form>
        </div>
      </div>

      <div className="flex h-[100px] p-8">
        <div className="">
          <button
            key={`edit-button`}
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`flex items-center justify-between px-6 py-3 rounded-full font-medium transition-colors duration-200 text-xs bg-green01 text-black hover:bg-green02 ${
              isSubmitting ? 'bg-zinc-600 text-zinc-400 cursor-not-allowed' : 'text-black hover:bg-green01'
            }`}
            style={{
              minWidth: '70px',
              height: '36px',
            }}
          >
            <span>{isSubmitting ? t('common.saving') : t('common.save')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
